import isEqual from "lodash/isEqual";
import FeatureFlags from "../FeatureFlags";

export const config = {
  /*
  // Правило
  behaviourTrigger: {
    // Активно/Не активно правило.
    enabled: boolean,
    // Нужно сохранять данные в localStorage или нет.
    persist: boolean,
    // Ключ в localStorage.
    storageKey: string,
    // Сколько раз выполнять правило. Если не указан — неограниченное количество раз.
    // Например, repeat=2 и counter=3:
    // - дождаться трёх триггеров
    // - вызвать onComplete
    // - повторить 1 раз
    repeat: number,
    // Условия для выполнения правила. Внутри должен быть либо counter, либо sequence.
    conditions: {
      // Сколько раз нужно вызвать функцию триггера, чтобы правило выполнилось.
      counter: number,
      // Последовательность действий, необходимых для выполнения правила.
      sequence: Array<string>,
      // Сколько раз подряд нужно вызвать функцию триггера с одинаковыми параметрами,
      // чтобы правило выполнилось.
      setOf: number,
      // Обязательно ли строгое следование вызовов триггера подряд. Если нет, то сбрасываем
      // счётчик/последовательность.
      strict: boolean
    },
    // Если указаны params, то для выполнения правила они должны совпадать с теми, что придут
    // в триггере
    params: {}
  }
  */

  // Сделать 3 сделки с одинаковым интервалом и проиграть их.
  sameIntervalDeals: {
    enabled: true,
    persist: true,
    storageKey: "sameIntervalDealsTrigger",
    repeat: 1,
    conditions: {
      setOf: 3
    },
    params: {
      profitable: false
    }
  },
  // Сделать 5 сделок на одном активе и проиграть их.
  sameStockDeals: {
    enabled: true,
    persist: true,
    storageKey: "sameStockDealsTrigger",
    repeat: 1,
    conditions: {
      setOf: 5
    },
    params: {
      profitable: false
    }
  },
  // Выиграть сделку с профитом меньше 80% (для VIP — 90%).
  lowProfitWin: {
    enabled: true,
    persist: true,
    storageKey: "lowProfitWinTrigger",
    repeat: 1,
    conditions: {
      counter: 1
    },
    params: {
      profitable: true,
      percent: 80,
      vipPercent: 90,
      lowPercent: true
    }
  },
  // Проиграть более 80% баланса.
  bigLoss: {
    enabled: true,
    persist: true,
    storageKey: "bigLossTrigger",
    repeat: 1,
    conditions: {
      counter: 1
    },
    params: {
      profitable: false,
      percent: 80,
      lowPercent: true
    }
  },

  // Примеры

  // Сделать 3 любых сделки.
  threeDealsInARow: {
    enabled: false,
    persist: true,
    storageKey: "threeDealsInARowTrigger",
    conditions: {
      counter: 3
    }
  },
  // Сделать 2 сделки Вверх и одну — Вниз. Выполнить правило 3 раза.
  twoUpOneDown: {
    enabled: false,
    persist: true,
    storageKey: "twoUpOneDownTrigger",
    repeat: 3,
    conditions: {
      sequence: ["up", "up", "down"]
    }
  },
  // Переключить тему 2 раза и после сделать 2 сделки. Повторение правила не ограничено.
  twoDealsAfterThemeSwitch: {
    enabled: false,
    persist: false,
    conditions: {
      sequence: ["theme", "theme", "deal", "deal"]
    }
  },
  // Добавить индикатор, удалить индикатор, добавить индикатор, удалить индикатор. Выполнить 1 раз.
  addAndRemoveIndicators: {
    enabled: false,
    persist: false,
    storageKey: "addAndRemoveIndicatorsTrigger",
    repeat: 1,
    conditions: {
      sequence: ["add", "remove", "add", "remove"]
    }
  },
  // Добавить индикатор, удалить индикатор. Выполнить 2 раза.
  addAndRemoveIndicatorsTwice: {
    enabled: false,
    persist: false,
    repeat: 2,
    storageKey: "addAndRemoveIndicatorsTwiceTrigger",
    conditions: {
      sequence: ["add", "remove"]
    }
  }
};

export const runBehaviourTrigger = (config, onComplete, params) => {
  const defaultStorage = {
    value: undefined,
    completed: false,
    canceled: false,
    sequence: 0
  };

  let counter = 0;
  let sequence = config.conditions.sequence && [...config.conditions.sequence];
  let set = [];
  let storage = defaultStorage;
  let completed = false;
  let canceled = false;

  const storageKeyPrefix = (params && params.storageKeyPrefix) + ":" || "";

  if (config.persist) {
    try {
      storage = JSON.parse(localStorage.getItem(`${storageKeyPrefix}${config.storageKey}`)) || defaultStorage;
      if (config.conditions.counter) {
        counter = storage.value || 0;
      } else if (config.conditions.sequence) {
        sequence = storage.value || [...config.conditions.sequence];
      }

      completed = storage.completed;
      canceled = storage.canceled;
      set = storage.value || [];
    } catch (error) {
      console.warn(error);
    }
  }


  const triggerFn = (val, triggerParams) => {
    let didTrigger = false;
    if (config.persist) {
      try {
        storage = JSON.parse(localStorage.getItem(`${storageKeyPrefix}${config.storageKey}`)) || defaultStorage;
        if (config.conditions.counter) {
          counter = storage.value || 0;
        } else if (config.conditions.sequence) {
          sequence = storage.value || [...config.conditions.sequence];
        }

        completed = storage.completed;
        canceled = storage.canceled;
        set = storage.value || [];
      } catch (error) {
        console.warn(error);
      }
    }

    if (!FeatureFlags.enableTriggers || !config.enabled || completed || canceled) return null;

    if (config.conditions.counter) {
      if (config.params && !isEqual(config.params, triggerParams)) {
        counter = config.conditions.strict ? 0 : counter;
      } else {
        counter += 1;
      }


      if (counter === config.conditions.counter) {
        if (typeof onComplete === "function") {
          didTrigger = true;
          onComplete();
        }

        completed = true;
        storage.sequence += 1;

        if (config.repeat === undefined || storage.sequence < config.repeat) {
          counter = 0;
          completed = false;
        }

        if (config.persist) {
          try {
            localStorage.setItem(`${storageKeyPrefix}${config.storageKey}`, JSON.stringify({
              value: counter,
              completed: completed,
              canceled: canceled,
              sequence: storage.sequence
            }));
          } catch (error) {
            console.warn(error);
          }
        }
      } else if (counter > config.conditions.counter) {
        completed = true;
      } else {
        if (config.persist) {
          try {
            localStorage.setItem(`${storageKeyPrefix}${config.storageKey}`, JSON.stringify({
              value: counter,
              completed: completed,
              canceled: canceled,
              sequence: storage.sequence
            }));
          } catch (error) {
            console.warn(error);
          }
        }
      }
    } else if (config.conditions.sequence) {
      if (config.params && !isEqual(config.params, triggerParams)) {
        sequence = config.conditions.strict ? [...config.conditions.sequence] : sequence;
      } else {
        if (sequence[0] && sequence[0] === val) {
          sequence.shift();
        }
      }

      if (sequence.length === 0) {
        if (typeof onComplete === "function") {
          didTrigger = true;
          onComplete();
        }

        completed = true;
        storage.sequence += 1;

        if (config.repeat === undefined || storage.sequence < config.repeat) {
          sequence = [...config.conditions.sequence];
          completed = false;
        }
      }

      if (config.persist) {
        try {
          localStorage.setItem(`${storageKeyPrefix}${config.storageKey}`, JSON.stringify({
            value: sequence,
            completed: completed,
            canceled: canceled,
            sequence: storage.sequence
          }));
        } catch (error) {
          console.warn(error);
        }
      }
    } else if (config.conditions.setOf) {
      if (config.params && !isEqual(config.params, triggerParams)) {
        set = [];
      } else if (set.length < config.conditions.setOf) {
        if (set[0] === val) {
          set.push(val);
        } else {
          set = [val];
        }
      } else {
        // TODO нужна ли эта ветвь?
        set.shift();

        if (set[0] === val) {
          set.push(val);
        } else {
          set = [val];
        }
      }

      if (set.length === config.conditions.setOf) {
        if (typeof onComplete === "function") {
          didTrigger = true;
          onComplete();
        }

        completed = true;
        storage.sequence += 1;

        if (config.repeat === undefined || storage.sequence < config.repeat) {
          set = [];
          completed = false;
        }
      }

      if (config.persist) {
        try {
          localStorage.setItem(`${storageKeyPrefix}${config.storageKey}`, JSON.stringify({
            value: set,
            completed: completed,
            canceled: canceled,
            sequence: storage.sequence
          }));
        } catch (error) {
          console.warn(error);
        }
      }
    }

    return didTrigger;
  };

  const triggerItself = {
    ...config,
    trigger: triggerFn,
    clearStorage: () => {
      if (config.persist) {
        try {
          localStorage.removeItem(`${storageKeyPrefix}${config.storageKey}`);
        } catch (error) {
          console.warn(error);
        }
      }
    },
    cancel: () => {
      canceled = true;
      if (config.persist) {
        try {
          storage = JSON.parse(localStorage.getItem(`${storageKeyPrefix}${config.storageKey}`)) || defaultStorage;
          storage.canceled = canceled;
          localStorage.setItem(`${storageKeyPrefix}${config.storageKey}`, JSON.stringify(storage));
        } catch (error) {
          console.warn(error);
        }
      }
    }
  };

  if (!config.enabled) triggerItself.clearStorage();

  return [triggerItself, triggerFn];
};



// WEBPACK FOOTER //
// ./src/configs/BehaviourTriggers.js